import type { ResolvedCatalogModel } from '../../../catalog';
import type { AppLocale } from '../../../i18n';
import type {
  FocusFilterResult,
  PresentationItemRate,
  PresentationModel,
  PresentationRecipePlan,
} from '../../../presentation';
import { buildRecipeFlowDisplay } from '../../shared/recipeDisplay';
import { getIconColor } from '../../shared/iconRegistry';
import type { Node, Edge } from '@xyflow/react';
import { formatCompactItemRateLabel, isPowerItem } from '../workbenchHelpers';
import { addViewRawFlowInputs } from './viewRawFlowGraph';

export interface RecipeNodeData {
  [key: string]: unknown;
  buildingName: string;
  buildingIconKey?: string;
  buildingCount: number;
  exactBuildingCount: number;
  recipeName: string;
  atlasIds?: string[];
  inputItemIds: string[];
  outputItemIds: string[];
  dimmed?: boolean;
}

export interface ExternalNodeData {
  [key: string]: unknown;
  itemId: string;
  itemName: string;
  iconKey?: string;
  ratePerMin: number;
  rateLabel: string;
  kind: 'input' | 'output';
  viewRawInput?: boolean;
  boundaryLabel?: string;
  atlasIds?: string[];
  dimmed?: boolean;
}

export interface ItemEdgeData {
  [key: string]: unknown;
  itemId: string;
  itemName: string;
  iconKey?: string;
  ratePerMin: number;
  rateLabel: string;
  width: number;
  color: string;
  viewRawInput?: boolean;
  dimmed?: boolean;
}

export type FlowGraphNode = Node<RecipeNodeData, 'recipe'> | Node<ExternalNodeData, 'external'>;
export type FlowGraphEdge = Edge<ItemEdgeData>;

const EPSILON = 1e-6;
const MIN_EDGE_WIDTH = 1;
const MAX_EDGE_WIDTH = 18;
const DEFAULT_EDGE_WIDTH = 4;

function hashString(str: string): number {
  let hash = 0;
  for (const ch of str) {
    hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  }
  return hash;
}

function fallbackItemColor(itemName: string): string {
  const hue = hashString(itemName) % 360;
  return `hsl(${hue} 55% 48%)`;
}

export function resolveItemColor(iconKey: string | undefined, itemName: string, atlasIds?: string[]): string {
  return getIconColor(iconKey, atlasIds) ?? fallbackItemColor(itemName);
}

function computeEdgeWidth(rate: number, maxNodeThroughput: number): number {
  if (maxNodeThroughput < EPSILON) {
    return DEFAULT_EDGE_WIDTH;
  }

  return Math.min(MAX_EDGE_WIDTH, Math.max(MIN_EDGE_WIDTH, MAX_EDGE_WIDTH * Math.sqrt(rate / maxNodeThroughput)));
}

interface ProducerInfo {
  nodeId: string;
  outputRate: number;
}

interface RawEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  itemId: string;
  itemName: string;
  iconKey?: string;
  ratePerMin: number;
}

export function buildFlowGraphData(
  recipePlans: PresentationRecipePlan[],
  catalog: ResolvedCatalogModel | null,
  model: PresentationModel,
  locale: AppLocale = 'zh-CN',
  focusResult?: FocusFilterResult | null,
  viewRawInputItemIds: readonly string[] = [...(focusResult?.viewRawInputItemIds ?? [])],
): { nodes: FlowGraphNode[]; edges: FlowGraphEdge[] } {
  if (recipePlans.length === 0) {
    return { nodes: [], edges: [] };
  }

  const atlasIds = model.catalogSummary.iconAtlasIds;
  const powerItemId = catalog?.powerItemId;
  const viewRawInputIds = new Set(viewRawInputItemIds);
  const nodes: FlowGraphNode[] = [];
  const rawEdges: RawEdge[] = [];

  // Build recipe nodes with filtered inputs
  const recipeVisibleInputs: PresentationItemRate[][] = [];
  for (let i = 0; i < recipePlans.length; i++) {
    const plan = recipePlans[i];
    const flowDisplay = buildRecipeFlowDisplay(catalog, plan);
    const visibleIds = new Set(flowDisplay.visibleInputs.map(input => input.itemId));
    const visibleInputs = plan.inputs.filter(input =>
      !isPowerItem(input.itemId, powerItemId) && (visibleIds.has(input.itemId) || viewRawInputIds.has(input.itemId))
    );
    recipeVisibleInputs.push(visibleInputs);

    const inputItemIds = visibleInputs
      .filter(inp => inp.ratePerMin > EPSILON)
      .map(inp => inp.itemId);
    const outputItemIds = plan.outputs
      .filter(out => out.ratePerMin > EPSILON && !isPowerItem(out.itemId, powerItemId))
      .map(out => out.itemId);

    nodes.push({
      id: `recipe-${i}`,
      type: 'recipe',
      position: { x: 0, y: 0 },
      data: {
        buildingName: plan.buildingName,
        buildingIconKey: plan.buildingIconKey,
        buildingCount: plan.roundedUpBuildingCount,
        exactBuildingCount: plan.exactBuildingCount,
        recipeName: plan.recipeName,
        atlasIds,
        inputItemIds,
        outputItemIds,
      },
    });
  }

  // Collect all produced items → producer nodes
  const producersByItem = new Map<string, ProducerInfo[]>();
  for (let i = 0; i < recipePlans.length; i++) {
    for (const output of recipePlans[i].outputs) {
      if (output.ratePerMin < EPSILON || isPowerItem(output.itemId, powerItemId)) continue;
      let producers = producersByItem.get(output.itemId);
      if (!producers) {
        producers = [];
        producersByItem.set(output.itemId, producers);
      }
      producers.push({ nodeId: `recipe-${i}`, outputRate: output.ratePerMin });
    }
  }

  // Determine which items are external inputs / target+surplus outputs
  const externalInputIds = new Set(model.externalInputs.map(e => e.itemId));
  const targetOutputIds = new Set(model.targets.map(t => t.itemId));
  const surplusOutputIds = new Set(model.surplusOutputs.map(s => s.itemId));
  const outputIds = new Set([...targetOutputIds, ...surplusOutputIds]);

  // Create external input nodes
  const createdExternalNodes = new Set<string>();
  for (const ext of model.externalInputs) {
    if (ext.ratePerMin < EPSILON || isPowerItem(ext.itemId, powerItemId)) continue;
    const nodeId = `input-${ext.itemId}`;
    if (!createdExternalNodes.has(nodeId)) {
      createdExternalNodes.add(nodeId);
      nodes.push({
        id: nodeId,
        type: 'external',
        position: { x: 0, y: 0 },
        data: {
          itemId: ext.itemId,
          itemName: ext.itemName,
          iconKey: ext.iconKey,
          ratePerMin: ext.ratePerMin,
          rateLabel: formatCompactItemRateLabel(ext.itemId, ext.ratePerMin, locale, powerItemId),
          kind: 'input',
          atlasIds,
        },
      });
    }
  }

  // Create target/surplus output nodes
  const outputRateByItem = new Map<string, { itemName: string; iconKey?: string; ratePerMin: number }>();
  for (const t of model.targets) {
    if (isPowerItem(t.itemId, powerItemId)) continue;
    const existing = outputRateByItem.get(t.itemId);
    outputRateByItem.set(t.itemId, {
      itemName: t.itemName,
      iconKey: t.iconKey,
      ratePerMin: (existing?.ratePerMin ?? 0) + t.actualRatePerMin,
    });
  }
  for (const s of model.surplusOutputs) {
    if (isPowerItem(s.itemId, powerItemId)) continue;
    const existing = outputRateByItem.get(s.itemId);
    outputRateByItem.set(s.itemId, {
      itemName: existing?.itemName ?? s.itemName,
      iconKey: existing?.iconKey ?? s.iconKey,
      ratePerMin: (existing?.ratePerMin ?? 0) + s.ratePerMin,
    });
  }
  for (const [itemId, info] of outputRateByItem) {
    if (info.ratePerMin < EPSILON) continue;
    const nodeId = `output-${itemId}`;
    if (!createdExternalNodes.has(nodeId)) {
      createdExternalNodes.add(nodeId);
      nodes.push({
        id: nodeId,
        type: 'external',
        position: { x: 0, y: 0 },
        data: {
          itemId,
          itemName: info.itemName,
          iconKey: info.iconKey,
          ratePerMin: info.ratePerMin,
          rateLabel: formatCompactItemRateLabel(itemId, info.ratePerMin, locale, powerItemId),
          kind: 'output',
          atlasIds,
        },
      });
    }
  }

  // Build edges: recipe inputs ← producers or external inputs
  for (let i = 0; i < recipePlans.length; i++) {
    const consumerNodeId = `recipe-${i}`;
    for (const input of recipeVisibleInputs[i]) {
      if (input.ratePerMin < EPSILON) continue;

      const producers = producersByItem.get(input.itemId);
      if (producers && producers.length > 0) {
        const totalProducerRate = producers.reduce((sum, p) => sum + p.outputRate, 0);
        for (const producer of producers) {
          const share = totalProducerRate > EPSILON ? producer.outputRate / totalProducerRate : 1 / producers.length;
          const edgeRate = input.ratePerMin * share;
          if (edgeRate < EPSILON) continue;
          rawEdges.push({
            id: `${producer.nodeId}->${consumerNodeId}::${input.itemId}`,
            source: producer.nodeId,
            target: consumerNodeId,
            sourceHandle: producer.nodeId.startsWith('recipe-') ? `out-${input.itemId}` : undefined,
            targetHandle: `in-${input.itemId}`,
            itemId: input.itemId,
            itemName: input.itemName,
            iconKey: input.iconKey,
            ratePerMin: edgeRate,
          });
        }
      }

      // External input edge
      if (externalInputIds.has(input.itemId)) {
        const extNodeId = `input-${input.itemId}`;
        if (createdExternalNodes.has(extNodeId)) {
          const consumers: { nodeId: string; rate: number }[] = [];
          for (let j = 0; j < recipePlans.length; j++) {
            const inputEntry = recipeVisibleInputs[j].find(inp => inp.itemId === input.itemId);
            if (inputEntry && inputEntry.ratePerMin > EPSILON) {
              consumers.push({ nodeId: `recipe-${j}`, rate: inputEntry.ratePerMin });
            }
          }
          const totalConsumerRate = consumers.reduce((sum, c) => sum + c.rate, 0);
          const extRate = model.externalInputs.find(e => e.itemId === input.itemId)?.ratePerMin ?? 0;
          if (extRate > EPSILON && totalConsumerRate > EPSILON) {
            const share = input.ratePerMin / totalConsumerRate;
            const edgeRate = extRate * share;
            if (edgeRate > EPSILON) {
              rawEdges.push({
                id: `${extNodeId}->${consumerNodeId}::${input.itemId}`,
                source: extNodeId,
                target: consumerNodeId,
                targetHandle: `in-${input.itemId}`,
                itemId: input.itemId,
                itemName: input.itemName,
                iconKey: input.iconKey,
                ratePerMin: edgeRate,
              });
            }
          }
        }
      }
    }
  }

  // Build edges: recipe outputs → target/surplus output nodes
  for (let i = 0; i < recipePlans.length; i++) {
    const producerNodeId = `recipe-${i}`;
    for (const output of recipePlans[i].outputs) {
      if (output.ratePerMin < EPSILON) continue;
      if (!outputIds.has(output.itemId)) continue;

      const outNodeId = `output-${output.itemId}`;
      if (!createdExternalNodes.has(outNodeId)) continue;

      const producers = producersByItem.get(output.itemId);
      const totalProducerRate = producers?.reduce((sum, p) => sum + p.outputRate, 0) ?? 0;
      const outputInfo = outputRateByItem.get(output.itemId);
      if (!outputInfo || outputInfo.ratePerMin < EPSILON || totalProducerRate < EPSILON) continue;

      const share = output.ratePerMin / totalProducerRate;
      const edgeRate = outputInfo.ratePerMin * share;
      if (edgeRate < EPSILON) continue;

      rawEdges.push({
        id: `${producerNodeId}->${outNodeId}::${output.itemId}`,
        source: producerNodeId,
        target: outNodeId,
        sourceHandle: `out-${output.itemId}`,
        itemId: output.itemId,
        itemName: output.itemName,
        iconKey: output.iconKey,
        ratePerMin: edgeRate,
      });
    }
  }

  // Deduplicate edges (same source→target for same item)
  const edgeMap = new Map<string, RawEdge>();
  for (const edge of rawEdges) {
    const key = `${edge.source}->${edge.target}::${edge.itemId}`;
    const existing = edgeMap.get(key);
    if (existing) {
      existing.ratePerMin += edge.ratePerMin;
    } else {
      edgeMap.set(key, { ...edge, id: key });
    }
  }

  const dedupedEdges = Array.from(edgeMap.values());

  // Compute max node throughput (max of total-in or total-out per node)
  const nodeThroughput = new Map<string, { inFlow: number; outFlow: number }>();
  for (const e of dedupedEdges) {
    const src = nodeThroughput.get(e.source) ?? { inFlow: 0, outFlow: 0 };
    src.outFlow += e.ratePerMin;
    nodeThroughput.set(e.source, src);
    const tgt = nodeThroughput.get(e.target) ?? { inFlow: 0, outFlow: 0 };
    tgt.inFlow += e.ratePerMin;
    nodeThroughput.set(e.target, tgt);
  }
  let maxNodeThroughput = 0;
  for (const t of nodeThroughput.values()) {
    maxNodeThroughput = Math.max(maxNodeThroughput, t.inFlow, t.outFlow);
  }

  const edges: FlowGraphEdge[] = dedupedEdges.map(e => {
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: 'item',
      data: {
        itemId: e.itemId,
        itemName: e.itemName,
        iconKey: e.iconKey,
        ratePerMin: e.ratePerMin,
        rateLabel: formatCompactItemRateLabel(e.itemId, e.ratePerMin, locale, powerItemId),
        width: computeEdgeWidth(e.ratePerMin, maxNodeThroughput),
        color: resolveItemColor(e.iconKey, e.itemName, atlasIds),
      },
    };
  });

  return applyFlowGraphFocus(
    addViewRawFlowInputs({ nodes, edges }, recipePlans, model, viewRawInputIds, locale, powerItemId),
    focusResult, locale, powerItemId,
  );
}

/** Focus changes preserve layout positions, handles and flow quantities. */
export function applyFlowGraphFocus(
  graph: { nodes: FlowGraphNode[]; edges: FlowGraphEdge[] },
  focusResult?: FocusFilterResult | null,
  locale: AppLocale = 'zh-CN',
  powerItemId?: string | null,
): { nodes: FlowGraphNode[]; edges: FlowGraphEdge[] } {
  const nodes = graph.nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      dimmed: !!focusResult && (node.type === 'recipe'
        ? !focusResult.focusedRecipeIndices.has(Number(node.id.slice('recipe-'.length)))
        : !focusResult.focusedItemIds.has(node.data.itemId) || (
            node.data.kind === 'input' && !node.data.viewRawInput && focusResult.viewRawInputItemIds.has(node.data.itemId)
          )),
    },
  })) as FlowGraphNode[];
  const dimmedNodeIds = new Set(nodes.filter(node => node.data.dimmed).map(node => node.id));
  const edges = graph.edges.map(edge => ({
    ...edge,
    data: {
      ...edge.data!,
      dimmed: !!focusResult && (
        !focusResult.focusedItemIds.has(edge.data!.itemId) ||
        (focusResult.viewRawInputItemIds.has(edge.data!.itemId) && !edge.data!.viewRawInput) ||
        dimmedNodeIds.has(edge.source) || dimmedNodeIds.has(edge.target)
      ),
    },
  }));
  for (const node of nodes) {
    if (node.type !== 'external' || !node.data.viewRawInput) continue;
    const rate = edges.reduce((sum, edge) => sum + (edge.source === node.id && !edge.data.dimmed ? edge.data.ratePerMin : 0), 0);
    node.data.ratePerMin = rate;
    node.data.rateLabel = formatCompactItemRateLabel(node.data.itemId, rate, locale, powerItemId);
    node.data.dimmed = rate < EPSILON;
  }
  return { nodes, edges };
}
